import { type HTMLAttributes, forwardRef } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/utils/cn';
import { getTagStyles } from '@/utils/colors';

// ============================================
// TYPES
// ============================================

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'outline' | 'secondary' | 'destructive';
  size?: 'sm' | 'md';
}

// ============================================
// STYLES
// ============================================

const variantStyles = {
  default: 'bg-brand-100 text-brand-800',
  outline: 'border border-[var(--border-primary)] text-[var(--text-secondary)] bg-transparent',
  secondary: 'bg-[var(--bg-secondary)] text-[var(--text-primary)]',
  destructive: 'bg-red-100 text-red-800',
};

const sizeStyles = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-0.5',
};

// ============================================
// BADGE
// ============================================

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant = 'default', size = 'sm', ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'inline-flex items-center rounded-full font-medium',
          variantStyles[variant],
          sizeStyles[size],
          className
        )}
        {...props}
      />
    );
  }
);
Badge.displayName = 'Badge';

// ============================================
// TAG BADGE (with automatic color)
// ============================================

interface TagBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'onClick'> {
  tag: string;
  onClick?: ((tag: string) => void) | undefined;
  onRemove?: ((tag: string) => void) | undefined;
  removable?: boolean | undefined;
  size?: 'sm' | 'md' | undefined;
}

export function TagBadge({
  tag,
  onClick,
  onRemove,
  removable,
  size = 'sm',
  className,
  ...props
}: TagBadgeProps) {
  const colorStyles = getTagStyles(tag);

  const handleClick = () => {
    if (onClick) {
      onClick(tag);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(tag);
    }
  };

  return (
    <span
      style={colorStyles}
      className={cn(
        'inline-flex items-center rounded-full font-medium',
        sizeStyles[size],
        onClick && 'cursor-pointer hover:opacity-80',
        className
      )}
      onClick={onClick ? handleClick : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleClick();
              }
            }
          : undefined
      }
      {...props}
    >
      {tag}
      {removable && onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          className="ml-1 rounded-full p-0.5 hover:bg-black/10 focus:outline-none"
          aria-label={`Remove ${tag} tag`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// ============================================
// COUNT BADGE (for category counts)
// ============================================

interface CountBadgeProps {
  count: number;
  className?: string;
}

export function CountBadge({ count, className }: CountBadgeProps) {
  if (count === 0) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5',
        'rounded-full text-xs font-medium bg-[var(--bg-tertiary)] text-[var(--text-secondary)]',
        className
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// ============================================
// STATUS BADGE
// ============================================

interface StatusBadgeProps {
  status: 'active' | 'inactive' | 'pending' | 'error';
  label?: string;
  className?: string;
}

const statusStyles = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-[var(--bg-secondary)] text-[var(--text-primary)]',
  pending: 'bg-yellow-100 text-yellow-800',
  error: 'bg-red-100 text-red-800',
};

const statusDotStyles = {
  active: 'bg-green-500',
  inactive: 'bg-[var(--text-tertiary)]',
  pending: 'bg-yellow-500',
  error: 'bg-red-500',
};

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium',
        statusStyles[status],
        className
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', statusDotStyles[status])} />
      {label ?? status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}
